import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'pl', label: 'PL' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language?.split('-')[0] || 'en';
  }, [i18n.language]);

  const change = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
    setOpen(false);
  };

  const current = languages.find((l) => i18n.language?.startsWith(l.code)) || languages[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap"
        aria-label="Change language"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-global-line"></i>
        </span>
        {current.label}
        <span className={`flex h-4 w-4 items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line"></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-32 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
          {languages.map((l) => {
            const active = current.code === l.code;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => change(l.code)}
                className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-primary-100 font-semibold text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {l.label}
                {active && (
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-check-line"></i>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}